import { Injectable } from '@angular/core';

declare let THREE: any;
import { Select3dService } from '../../../services/select-3d.service';
import { SceneUtilsService } from '../../../services/scene-utils.service';
import { KeyStateService } from '../../../services/key-state.service';
import { StageService } from '../../../services/stage.service';
import { OrbitService } from '../../../services/orbit.service';
import { ComponentBase } from '../../../component-base';
import { RayHelperService } from '../../../services/ray-helper.service';
import { DragBehaviorBase } from './Behavior/drag-behavior-base';
import { FloorObjectDragBehaviourFactory } from './Behavior/FloorObject/floor-object-drag';
import { FloorObjectSmartDragHandlerFactory } from './Handler/floor-object-smart-drag';
import { WallEmbeddableObjectDragFactory } from './Behavior/WallObject/wall-embeddable-object-drag';
import { MoveCameraService } from '../../2D/move-camera.service';
import { CameraService } from '../../../services/camera.service';
import { WallObjectSmartDragHandlerFactory } from './Handler/wall-object-smart-drag';
import { KeepObjectInWallBoundsHandlerFactory } from './Handler/keep-object-in-wall-bounds-handler';
import { ObjectWorldTransform } from './Class/ObjectWorldTransform';
import { TransformationQueue } from './Class/TransformationQueue';
import { EventLogService, Actions } from '../../../services/eventLog';
import { AwningDragFactory } from './Behavior/WallObject/awning-drag';
import { KeepAwningOnWallBoundsHandlerFactory } from './Handler/keep-awning-on-wall-area-handler';
import { WallMountedObjectDragFactory } from './Behavior/WallObject/wall-mounted-object-drag';
import { KeepMountableOnWallBoundsHandlerFactory } from './Handler/keep-mountable-on-wall-area-handler';

@Injectable()
export class Drag3D extends ComponentBase {
  protected dragBehaviours: DragBehaviorBase[];
  protected currentDragBehaviour: DragBehaviorBase | null;
  protected isDrag: boolean;
  protected rayCaster: any;
  protected staticObjects: any[];
  private selectedObjects: any[] = [];
  private objectWorldTransform = new ObjectWorldTransform();
  private transformationQueue = new TransformationQueue();
  /** Touch drag: keep one active finger to avoid OrbitControls conflicts. */
  private _dragTouchId: number | null = null;

  constructor(
    protected stageService: StageService,
    protected keyStateService: KeyStateService,
    protected sceneUtilsService: SceneUtilsService,
    protected select3DService: Select3dService,
    protected orbitService: OrbitService,
    protected rayHelperSrvc: RayHelperService,
    floorObjectDragBehaviourFactory: FloorObjectDragBehaviourFactory,
    floorObjectSmartDragHandlerFactory: FloorObjectSmartDragHandlerFactory,
    private moveCameraService: MoveCameraService,
    private cameraService: CameraService,
    wallEmbeddableObjectDragFactory: WallEmbeddableObjectDragFactory,
    awningDragFactory: AwningDragFactory,
    wallMountedObjectDragFactory: WallMountedObjectDragFactory,
    wallObjectSmartDragHandlerFactory: WallObjectSmartDragHandlerFactory,
    keepObjectInWallBoundsHandlerFactory: KeepObjectInWallBoundsHandlerFactory,
    keepAwningOnWallBoundsHandlerFactory: KeepAwningOnWallBoundsHandlerFactory,
    keepMountableOnWallBoundsHandlerFactory: KeepMountableOnWallBoundsHandlerFactory,
    private eventLogService: EventLogService
  ) {
    super();
    this.currentDragBehaviour = null;
    this.isDrag = false;
    this.rayCaster = new THREE.Raycaster();
    this.staticObjects = null;
    this.dragBehaviours = [
      floorObjectDragBehaviourFactory.create(floorObjectSmartDragHandlerFactory.create()),
      wallEmbeddableObjectDragFactory.create(
        wallObjectSmartDragHandlerFactory.create(keepObjectInWallBoundsHandlerFactory.create())
      ),
      awningDragFactory.create(keepAwningOnWallBoundsHandlerFactory.create()),
      wallMountedObjectDragFactory.create(keepMountableOnWallBoundsHandlerFactory.create())
    ];
  }

  private patchOffsetFromTouch(e: TouchEvent, touch: Touch): void {
    const target = e.target as HTMLElement;
    if (!target || !touch || !target.getBoundingClientRect) {
      return;
    }
    const rect = target.getBoundingClientRect();
    (e as any).offsetX = touch.clientX - rect.left;
    (e as any).offsetY = touch.clientY - rect.top;
  }

  private touchById(list: TouchList, id: number): Touch | null {
    if (!list || list.length === 0) {
      return null;
    }
    for (let i = 0; i < list.length; i++) {
      if (list[i].identifier === id) {
        return list[i];
      }
    }
    return null;
  }

  private performDragMove(e: any) {
    this.sceneUtilsService.getRay(e.offsetX, e.offsetY, this.rayCaster);

    const draggedObjects = this.selectedObjects;
    const dragBehaviour = this.currentDragBehaviour;
    let i = 0,
      draggedObject,
      transformation;

    for (; i < draggedObjects.length; i++) {
      draggedObject = draggedObjects[i];

      this.objectWorldTransform.setFromObject(draggedObject);

      transformation = new ObjectWorldTransform().copy(this.objectWorldTransform);

      dragBehaviour.move(
        {
          staticObjects: this.staticObjects,
          draggedObjects: draggedObjects,
          raycaster: this.rayCaster,
          draggedObject,
          draggedObjectWorldTransform: this.objectWorldTransform
        },
        transformation
      );

      this.transformationQueue.add(transformation);
    }

    this.transformationQueue.apply(draggedObjects);

    this.fireEvent('drag');

    return false;
  }

  touchStart(e: TouchEvent) {
    if (!this.isEnabled() || !e || !e.changedTouches || e.changedTouches.length === 0) {
      return;
    }
    // one-finger drag only; multi-touch reserved for camera gestures
    if (!e.targetTouches || e.targetTouches.length !== 1) {
      return;
    }
    const t = e.changedTouches[0];
    this._dragTouchId = t.identifier;
    this.patchOffsetFromTouch(e, t);

    this.mouseDown(e as any);

    if (this.currentDragBehaviour) {
      this.isDrag = true;
      if (e.cancelable) {
        e.preventDefault();
      }
      if ((e as any).stopPropagation) {
        (e as any).stopPropagation();
      }
      return false;
    }
  }

  touchMove(e: TouchEvent) {
    if (!this.isEnabled() || this._dragTouchId === null || !this.currentDragBehaviour) {
      return;
    }
    const t = this.touchById(e.targetTouches, this._dragTouchId);
    if (!t) {
      return;
    }
    this.patchOffsetFromTouch(e, t);
    if (e.cancelable) {
      e.preventDefault();
    }
    if ((e as any).stopPropagation) {
      (e as any).stopPropagation();
    }
    return this.performDragMove(e as any);
  }

  touchEnd(e: TouchEvent) {
    if (this._dragTouchId === null) {
      return;
    }
    let ended = false;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this._dragTouchId) {
        ended = true;
        break;
      }
    }
    if (!ended) {
      return;
    }
    this._dragTouchId = null;
    const hadDrag = !!this.currentDragBehaviour;
    this.mouseUp();
    if (hadDrag) {
      if (e.cancelable) {
        e.preventDefault();
      }
      if ((e as any).stopPropagation) {
        (e as any).stopPropagation();
      }
      return false;
    }
  }

  mouseDown(e) {
    this.selectedObjects = [
      ...this.select3DService.getSelectedAccessories(),
      ...this.select3DService.getSelectedWallEmbeddable(),
      ...this.select3DService.getSelectedAwnings(),
      ...this.select3DService.getSelectedTrellis()
    ];

    if (this.selectedObjects.length === 0) {
      return;
    }

    this.sceneUtilsService.getRay(e.offsetX, e.offsetY, this.rayCaster);

    const intersection = this.rayHelperSrvc.intersectObjectsObb(
      this.rayCaster.ray,
      this.selectedObjects
    )[0];

    if (intersection) {
      this.currentDragBehaviour = this.dragBehaviours.find(dragBehaviour =>
        dragBehaviour.canExecute(this.selectedObjects)
      );

      if (!this.currentDragBehaviour) {
        return;
      }

      this.orbitService.disable();
      this.moveCameraService.disable();

      const filter = this.currentDragBehaviour.getStaticObjectsFilter(this.selectedObjects);

      this.staticObjects = this.stageService.getChildrenByPredicate(filter);

      this.eventLogService.dispatch(new Actions.SaveObjectsTransformAction(this.selectedObjects));

      this.currentDragBehaviour.hold({
        intersection,
        raycaster: this.rayCaster,
        selectedObjects: this.selectedObjects
      });

      return;
    }
  }

  mouseMove(e) {
    this.isDrag = this.keyStateService.result.isMouseHold && !!this.currentDragBehaviour;

    if (this.isDrag) {
      return this.performDragMove(e);
    }
  }

  mouseUp() {
    if (this.currentDragBehaviour) {
      if (this.cameraService.isIn3D()) {
        this.orbitService.enable();
      }
      if (this.cameraService.isIn2D()) {
        this.moveCameraService.enable();
      }
    }

    if (this.isDrag) {
      this.currentDragBehaviour.release({
        staticObjects: this.staticObjects,
        draggedObjects: this.selectedObjects
      });

      this.selectedObjects = null;
      this.staticObjects.length = 0;
    }

    this.currentDragBehaviour = null;

    this.isDrag = false;

    this.fireEvent('dragEnd');
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/3D/Drag/drag3-d.service.ts